import { AriaColorSwatchProps, useColorSwatch } from "@react-aria/color";
import * as React from "react";
import { cx } from "../../utils/dom";
import { Flex, FlexProps } from "../flex";
import {
  Item,
  ItemProps,
  ListProps,
  ListState,
  Node,
  useListState,
} from "react-stately";
import { useListBox, useOption } from "react-aria";
import { Color as AriaColor, parseColor } from "@react-stately/color";
import { StyleProps } from "../../utils/types";
import { BorderRadius, SizeOption } from "../../utils/theme";
import { useTheme } from "../theme";

interface ColorOption {
  key: string;
  color: string;
  label?: string;
}

type ColorSwatchPickerProps<T> = FlexProps &
  ListProps<T> &
  StyleProps<"wrapper" | "swatch"> & {
    colors: Array<ColorOption>;
    onChange?: (color: AriaColor) => void;
    size?: SizeOption;
    radius?: BorderRadius;
  };

export function ColorSwatchPicker({
  colors,
  items,
  children,
  radius,
  size,
  onChange,
  selectionMode = "single",
  className,
  classNames,
  style,
  styles,
  ...props
}: ColorSwatchPickerProps<ColorOption>) {
  items = colors;
  children = (item) => (
    <Color
      textValue={item.color}
      color={parseColor(item.color)}
      key={item.color}
    />
  );

  const listProps = {
    ...props,
    items,
    children,
    selectionMode,
    ["aria-label"]: "color swatch picker",
    onSelectionChange: (keys) => {
      const keysToArray = [...keys];
      onChange?.(parseColor(keysToArray[0] as string));
      props.onSelectionChange?.(keys);
    },
  } as ListProps<ColorOption>;

  const theme = useTheme();
  const wrapperRef = React.useRef(null);

  const listState = useListState(listProps);
  const { listBoxProps } = useListBox(listProps, listState, wrapperRef);

  const _colors = [...listState.collection];

  radius = radius ?? theme?.radius ?? "md";

  return (
    <SwatchProvider colors={colors} radius={radius} size={size} {...props}>
      <Flex
        style={{ ...style, ...styles?.wrapper }}
        className={cx(className, classNames?.wrapper)}
        wrap="wrap"
        {...listBoxProps}
        ref={wrapperRef}
      >
        {_colors.map((color, index) => (
          <SwatchOption
            className={classNames?.swatch}
            style={styles?.swatch}
            key={index}
            state={listState}
            item={color}
          />
        ))}
      </Flex>
    </SwatchProvider>
  );
}

type SwatchOptionProps = StyleProps & {
  item: Node<any>;
  state: ListState<any>;
};

export function SwatchOption({
  item,
  state,
  style,
  className,
}: SwatchOptionProps) {
  const ref = React.useRef(null);
  const {
    optionProps,
    isDisabled,
    isFocusVisible,
    isFocused,
    isPressed,
    isSelected,
  } = useOption({ key: item.key }, state, ref);

  const color = item.props.color;
  const ctx = useSwatchPickerContext();

  return (
    <ColorSwatch
      className={className}
      style={style}
      color={color}
      _ref={ref}
      data-disabled={isDisabled}
      data-selected={isSelected}
      data-focused={isFocused}
      data-focus-visible={isFocusVisible}
      data-pressed={isPressed}
      data-radius={ctx.radius}
      data-size={ctx.size}
      {...optionProps}
    ></ColorSwatch>
  );
}

type ColorSwatchProps = AriaColorSwatchProps &
  StyleProps & {
    _ref?: React.RefObject<HTMLDivElement>;
  };

function ColorSwatch({
  className,
  style,
  _ref,
  color: colorProp,
  ...props
}: ColorSwatchProps) {
  const { colorSwatchProps, color } = useColorSwatch({
    ...props,
    color: colorProp,
  });
  return (
    <div
      {...props}
      ref={_ref}
      className={cx("p-color-swatch", className)}
      {...colorSwatchProps}
      style={{
        ...colorSwatchProps.style,
        background: color.toString("css"),
        ...style,
      }}
    >
      <div className="p-swatch-checkers"></div>
    </div>
  );
}

const SwatchPickerContext =
  React.createContext<ColorSwatchPickerProps<ColorOption> | null>(null);
function SwatchProvider(
  props: React.PropsWithChildren<ColorSwatchPickerProps<ColorOption>>
) {
  return (
    <SwatchPickerContext.Provider value={props}>
      {props.children}
    </SwatchPickerContext.Provider>
  );
}

function useSwatchPickerContext() {
  const ctx = React.useContext(SwatchPickerContext);
  if (!ctx) {
    throw new Error("SwatchPicker must be used within a SwatchPickerProvider");
  }
  return ctx;
}

const Color = Item as (
  props: Partial<ItemProps<ColorOption>> & { color: AriaColor }
) => JSX.Element;
